import { Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { RoomView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Input } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useCanEdit, useRequiredSession } from '../../../stores/session';
import { useRooms } from '../../interviews/api/interviews-api';
import { VentureColumn, type RoomVenture } from '../components/rooms/VentureColumn';
import { useRoomActions } from '../components/rooms/use-room-actions';
import styles from './RoomsPage.module.css';

/**
 * F10.14's rooms (S19 Q5): the meeting rooms in-person interviews book. Option A of the admin redesign
 * (`5-settings/rooms.html`): one column per venture, every venture the person works in included, even
 * one with no rooms yet; add at the bottom of a column, rename in the card, and Undo on add, rename and
 * take off. A room with interviews still ahead can't be taken off until they move. Admin and HR
 * (`hiring_setup`, ADR 0073).
 */
export function RoomsPage(): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const session = useRequiredSession();
  const rooms = useRooms(canEdit);
  const actions = useRoomActions();
  const [query, setQuery] = useState('');

  if (!canEdit) return <ForbiddenState resource="rooms" contact="Your admin" />;

  const all = rooms.data ?? [];
  const ventures = venturesOf(session.ventures, all);
  const needle = query.trim().toLowerCase();
  const isSearching = needle !== '';
  const matches = (room: RoomView): boolean => !isSearching || room.name.toLowerCase().includes(needle);
  const matchCount = all.filter(matches).length;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Rooms</h1>
          <p className={styles.lede}>Meeting rooms for in-person interviews. Booking one checks it isn’t already taken. Online and phone interviews don’t need one.</p>
        </div>
        <span className={styles.search}>
          <Input icon={Search} aria-label="Find a room" placeholder="Find a room" value={query} onChange={(event) => setQuery(event.target.value)} onClear={isSearching ? () => setQuery('') : undefined} />
        </span>
      </header>

      {rooms.isPending ? (
        <Card>
          <SkeletonLines count={4} />
        </Card>
      ) : rooms.error && !rooms.data ? (
        <ServerErrorState onRetry={() => void rooms.refetch()} />
      ) : ventures.length === 0 ? (
        <EmptyState title="No companies yet" body="Rooms belong to a company. Add one under Organisation, then its rooms here." action={null} />
      ) : (
        <>
          {isSearching && matchCount === 0 && (
            <p className={styles.noMatch} role="status">
              No room matches “{query.trim()}”.{' '}
              <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
                Clear the search
              </Button>
            </p>
          )}
          <div className={styles.columns}>
            {ventures.map((venture) => {
              const ventureRooms = all.filter((room) => room.ventureId === venture.id);
              return <VentureColumn key={venture.id} venture={venture} rooms={ventureRooms} shown={ventureRooms.filter(matches)} isSearching={isSearching} actions={actions} />;
            })}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The person's ventures, in the session's order, so a venture with no rooms still gets its column —
 * plus any venture a room belongs to that the session doesn't list, so no room is ever hidden.
 */
function venturesOf(sessionVentures: readonly RoomVenture[], rooms: readonly RoomView[]): readonly RoomVenture[] {
  const known = new Set(sessionVentures.map((venture) => venture.id));
  const extra = new Map<string, RoomVenture>();
  for (const room of rooms) {
    if (!known.has(room.ventureId) && !extra.has(room.ventureId)) extra.set(room.ventureId, { id: room.ventureId, name: room.ventureName, logoVersion: null });
  }
  return [...sessionVentures, ...extra.values()];
}
